import React, { useRef, useEffect } from 'react';

/**
 * ProgrammaticShape - Renders UI components using Canvas API instead of images
 * This allows for perfect vector scaling without pixelation
 */

const ProgrammaticShape = ({ 
  type = 'button', 
  width = 100, 
  height = 40, 
  style = {},
  content = {},
  state = 'default' // default, hover, active, disabled
}) => {
  const canvasRef = useRef();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const devicePixelRatio = window.devicePixelRatio || 1;

    // Set up high DPI rendering
    canvas.width = width * devicePixelRatio;
    canvas.height = height * devicePixelRatio;
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    ctx.scale(devicePixelRatio, devicePixelRatio);

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Render based on component type
    switch (type) {
      case 'button':
        drawButton(ctx, width, height, style, content, state);
        break;
      case 'toggle':
        drawToggle(ctx, width, height, style, content, state);
        break;
      case 'panel':
        drawPanel(ctx, width, height, style, content, state);
        break;
      case 'slider':
        drawSlider(ctx, width, height, style, content, state);
        break;
      default:
        drawButton(ctx, width, height, style, content, state);
    }
  }, [type, width, height, style, content, state]);

  return (
    <canvas 
      ref={canvasRef} 
      style={{ 
        display: 'block',
        imageRendering: 'auto' // Let browser optimize
      }} 
      aria-label={content.primaryText ? content.primaryText : 'UI shape'}
      role="img"
      tabIndex={-1}
    />
  );
};

// Button drawing function
function drawButton(ctx, width, height, style, content, state) {
  const borderRadius = style.borderRadius || 8;
  const backgroundColor = getStateColor(style.backgroundColor || '#007AFF', state);
  const borderColor = style.borderColor || 'transparent';
  const borderWidth = style.borderWidth || 0;

  // Draw button background with rounded corners
  ctx.beginPath();
  roundedRect(ctx, borderWidth/2, borderWidth/2, width - borderWidth, height - borderWidth, borderRadius);
  
  // Fill background
  ctx.fillStyle = backgroundColor;
  ctx.fill();

  // Draw border if specified
  if (borderWidth > 0) {
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    ctx.stroke();
  }

  // Add gradient overlay for depth
  if (state !== 'disabled') {
    ctx.beginPath();
    roundedRect(ctx, borderWidth/2, borderWidth/2, width - borderWidth, height - borderWidth, borderRadius);
    
    const gradient = ctx.createLinearGradient(0, 0, 0, height);
    gradient.addColorStop(0, state === 'active' ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.3)');
    gradient.addColorStop(1, state === 'active' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.1)');
    
    ctx.fillStyle = gradient;
    ctx.fill();
  }

  // Draw text if provided
  if (content.primaryText) {
    drawText(ctx, content.primaryText, width, height, style, state);
  }
}

// Toggle switch drawing function
function drawToggle(ctx, width, height, style, content, state) {
  const isOn = state === 'on' || state === 'active';
  const trackColor = isOn ? (style.activeColor || '#34C759') : (style.inactiveColor || '#E5E5E7');
  const thumbColor = style.thumbColor || '#FFFFFF';
  
  const trackHeight = height * 0.6;
  const trackY = (height - trackHeight) / 2;
  const thumbSize = trackHeight - 4;
  const thumbY = trackY + 2;
  
  // Draw track
  ctx.beginPath();
  roundedRect(ctx, 0, trackY, width, trackHeight, trackHeight / 2);
  ctx.fillStyle = trackColor;
  ctx.fill();

  // Draw thumb
  const thumbX = isOn ? width - thumbSize - 2 : 2;
  ctx.beginPath();
  ctx.arc(thumbX + thumbSize/2, thumbY + thumbSize/2, thumbSize/2, 0, 2 * Math.PI);
  ctx.fillStyle = thumbColor;
  ctx.fill();

  // Add shadow to thumb
  ctx.shadowColor = 'rgba(0,0,0,0.2)';
  ctx.shadowBlur = 2;
  ctx.shadowOffsetY = 1;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

// Panel drawing function
function drawPanel(ctx, width, height, style, content, state) {
  const borderRadius = style.borderRadius || 12;
  const backgroundColor = style.backgroundColor || '#F2F2F7';
  const borderColor = style.borderColor || '#E5E5E7';
  const borderWidth = style.borderWidth || 1;

  // Draw panel background
  ctx.beginPath();
  roundedRect(ctx, 0, 0, width, height, borderRadius);
  ctx.fillStyle = backgroundColor;
  ctx.fill();

  // Draw border
  if (borderWidth > 0) {
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    ctx.stroke();
  }

  // Add subtle inner shadow for depth
  ctx.beginPath();
  roundedRect(ctx, 1, 1, width - 2, height - 2, borderRadius - 1);
  const gradient = ctx.createLinearGradient(0, 0, 0, 10);
  gradient.addColorStop(0, 'rgba(0,0,0,0.05)');
  gradient.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = gradient;
  ctx.fill();
}

// Slider drawing function
function drawSlider(ctx, width, height, style, content, state) {
  const value = content.value || 0; // 0-1 range
  const trackHeight = 4;
  const trackY = (height - trackHeight) / 2;
  const thumbSize = 20;
  const activeColor = style.activeColor || '#007AFF';
  const trackColor = style.trackColor || '#E5E5E7';

  // Draw track
  ctx.beginPath();
  roundedRect(ctx, 0, trackY, width, trackHeight, trackHeight / 2);
  ctx.fillStyle = trackColor;
  ctx.fill();

  // Draw active track
  const activeWidth = width * value;
  if (activeWidth > 0) {
    ctx.beginPath();
    roundedRect(ctx, 0, trackY, activeWidth, trackHeight, trackHeight / 2);
    ctx.fillStyle = activeColor;
    ctx.fill();
  }

  // Draw thumb
  const thumbX = (width - thumbSize) * value;
  const thumbY = (height - thumbSize) / 2;
  
  ctx.beginPath();
  ctx.arc(thumbX + thumbSize/2, thumbY + thumbSize/2, thumbSize/2, 0, 2 * Math.PI);
  ctx.fillStyle = '#FFFFFF';
  ctx.fill();

  // Thumb border
  ctx.strokeStyle = activeColor;
  ctx.lineWidth = 2;
  ctx.stroke();

  // Thumb shadow
  ctx.shadowColor = 'rgba(0,0,0,0.2)';
  ctx.shadowBlur = 4;
  ctx.shadowOffsetY = 2;
  ctx.fill();
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;
}

// Helper function to draw rounded rectangles
function roundedRect(ctx, x, y, width, height, radius) {
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
}

// Helper function to get state-based colors
function getStateColor(baseColor, state) {
  switch (state) {
    case 'hover':
      return adjustBrightness(baseColor, 0.1);
    case 'active':
      return adjustBrightness(baseColor, -0.1);
    case 'disabled':
      return adjustOpacity(baseColor, 0.5);
    default:
      return baseColor;
  }
}

// Helper function to adjust color brightness
function adjustBrightness(color, amount) {
  // Simple brightness adjustment - in production you'd want a more sophisticated color manipulation library
  if (color.startsWith('#')) {
    const num = parseInt(color.slice(1), 16);
    const r = Math.min(255, Math.max(0, ((num >> 16) & 255) + Math.round(255 * amount)));
    const g = Math.min(255, Math.max(0, ((num >> 8) & 255) + Math.round(255 * amount)));
    const b = Math.min(255, Math.max(0, (num & 255) + Math.round(255 * amount)));
    return `rgb(${r}, ${g}, ${b})`;
  }
  return color;
}

// Helper function to adjust color opacity
function adjustOpacity(color, opacity) {
  if (color.startsWith('#')) {
    const num = parseInt(color.slice(1), 16);
    const r = (num >> 16) & 255;
    const g = (num >> 8) & 255;
    const b = num & 255;
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }
  return color;
}

// Helper function to draw text
function drawText(ctx, text, width, height, style, state) {
  const fontSize = style.fontSize || 16;
  const fontFamily = style.fontFamily || 'system-ui, -apple-system, sans-serif';
  const textColor = getStateColor(style.color || '#FFFFFF', state);
  
  ctx.fillStyle = textColor;
  ctx.font = `${fontSize}px ${fontFamily}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  
  // Handle text wrapping if needed
  const maxWidth = width - 16; // padding
  const words = text.split(' ');
  let line = '';
  const lines = [];
  
  for (const word of words) {
    const testLine = line + word + ' ';
    const metrics = ctx.measureText(testLine);
    
    if (metrics.width > maxWidth && line !== '') {
      lines.push(line.trim());
      line = word + ' ';
    } else {
      line = testLine;
    }
  }
  lines.push(line.trim());
  
  // Draw lines
  const lineHeight = fontSize * 1.2;
  const totalHeight = lines.length * lineHeight;
  const startY = (height - totalHeight) / 2 + fontSize / 2;
  
  lines.forEach((line, index) => {
    ctx.fillText(line, width / 2, startY + index * lineHeight);
  });
}

export default ProgrammaticShape;